/**
 * The styling of this device's own drafts, kept beside the drafts themselves.
 *
 * A draft post carries its styling in the same mark a published post carries
 * (`#/state/postStyle/types`): it is written into the draft post where the record
 * allows it, and copied here as well, on this device, for the case where it is not
 * kept. That case is not hypothetical — a draft is stored by a service that is not
 * this app, its posts are checked against the draft lexicon, and a field that
 * lexicon does not describe may simply not come back. The styling is the author's
 * work; a device that still has it should not lose it because a request described
 * it well and a response did not describe it back.
 *
 * So the mark is written twice and read from wherever it is found: the record
 * first, because that copy travels with the draft, and this mirror second, because
 * that copy survives. A draft that is deleted or published forgets its mirror
 * (`forgetDraftStyling`), so a device holds styling for exactly the drafts it
 * still has, and nothing else.
 */
import AsyncStorage from '@react-native-async-storage/async-storage'

import {logger} from './logger'

/** A styling as it is written down: the mark, unread until `readStyledPost`. */
export type DraftStylingMark = Record<string, unknown>

const KEY = '@taderok/drafts/styling/v1'

/**
 * Every draft this device has styling for, by draft id, one entry per post —
 * `null` where a post has none, so an index means the same post on the way in and
 * on the way out.
 */
type Mirror = Record<string, (DraftStylingMark | null)[]>

async function readMirror(): Promise<Mirror> {
  try {
    const raw = await AsyncStorage.getItem(KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Mirror) : {}
  } catch (e) {
    logger.warn('Failed to read the draft styling mirror', {error: e})
    return {}
  }
}

async function writeMirror(mirror: Mirror): Promise<void> {
  try {
    await AsyncStorage.setItem(KEY, JSON.stringify(mirror))
  } catch (e) {
    // Not an error the author can act on: the draft was saved, and its styling
    // still travels inside the draft post itself.
    logger.warn('Failed to write the draft styling mirror', {error: e})
  }
}

/** Remember the styling of a draft this device just saved. */
export async function rememberDraftStyling(
  draftId: string,
  styling: (DraftStylingMark | undefined)[],
): Promise<void> {
  const mirror = await readMirror()
  mirror[draftId] = styling.map(mark => mark ?? null)
  await writeMirror(mirror)
}

/** The styling of a draft this device saved, or `null` if it has none here. */
export async function recallDraftStyling(
  draftId: string,
): Promise<(DraftStylingMark | null)[] | null> {
  const mirror = await readMirror()
  const entry = mirror[draftId]
  return Array.isArray(entry) ? entry : null
}

/** Forget a draft — it has been deleted or published. */
export async function forgetDraftStyling(draftId: string): Promise<void> {
  const mirror = await readMirror()
  if (!(draftId in mirror)) return
  delete mirror[draftId]
  await writeMirror(mirror)
}
